import { useState, useEffect, useRef } from "react";
import { useStore } from "../store/Store";

// ─── "Liquid Glass" (iOS 26 style) shared tokens ───────────────────────────
const glass = {
  background: "rgba(255,255,255,0.55)",
  backdropFilter: "blur(24px) saturate(180%)",
  WebkitBackdropFilter: "blur(24px) saturate(180%)",
  border: "1px solid rgba(255,255,255,0.6)",
  boxShadow: "0 8px 32px rgba(31,38,135,0.10), inset 0 1px 0 rgba(255,255,255,0.7)",
};
const glassBtn = {
  background: "rgba(255,255,255,0.5)",
  backdropFilter: "blur(16px) saturate(180%)",
  WebkitBackdropFilter: "blur(16px) saturate(180%)",
  border: "1px solid rgba(255,255,255,0.65)",
  boxShadow: "0 2px 10px rgba(31,38,135,0.08), inset 0 1px 0 rgba(255,255,255,0.8)",
};

export default function Navbar({ page, onNav }) {
  const { cartQty, wish, user, products, logoUrl } = useStore();
  const [showSearch, setShowSearch] = useState(false);
  const [sq, setSq] = useState("");
  const [hidden, setHidden] = useState(false);
  const lastScroll = useRef(0);
  const [isMobile, setIsMobile] = useState(typeof window!=="undefined" ? window.innerWidth <= 900 : false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Logo is admin-editable (Admin Panel → Banners tab)

  // Reset visibility whenever the page changes (e.g. Home → Shop shouldn't stay hidden)
  useEffect(() => {
    setHidden(false);
    lastScroll.current = window.scrollY;
  }, [page, isMobile]);

  // Hide-on-scroll is a MOBILE-ONLY behavior — on desktop the nav always stays visible (just transparent).
  // The nav floats transparently over the hero photo (icons show directly on the image), and
  // tucks away only while actively scrolling down; scrolling up brings it right back.
  useEffect(() => {
    const onScroll = () => {
      if (!isMobile) { setHidden(false); return; }
      const cur = window.scrollY;
      if (cur > lastScroll.current && cur > 90) setHidden(true);
      else setHidden(false);
      lastScroll.current = cur;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isMobile]);

  const startVoice = () => {
    if (!('webkitSpeechRecognition' in window)) { alert("Voice search not supported in this browser"); return; }
    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = "hi-IN";
    recognition.onresult = (e) => {
      const text = e.results[0][0].transcript;
      setSq(text);
      setShowSearch(true);
      onNav("shop", null, text);
    };
    recognition.start();
  };

  const iconBtnStyle = { background:"none", border:"none", width:36, height:36, borderRadius:"50%", cursor:"pointer", fontSize:19, color:"#1a1a2e", display:"flex", alignItems:"center", justifyContent:"center", position:"relative", transition:"transform 0.15s ease", filter:"drop-shadow(0 1px 4px rgba(0,0,0,0.35))" };

  return (
    <>
      {!isMobile && (
      <nav style={{ position:"fixed", top:0, left:0, right:0, zIndex:1000, background:"none", border:"none", boxShadow:"none", height:64, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 clamp(14px,4vw,28px)", gap:8, transform:hidden?"translateY(-100%)":"translateY(0)", transition:"transform 0.35s cubic-bezier(0.4,0,0.2,1)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:16, flex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", flexShrink:0 }} onClick={()=>onNav("home")}>
            <img src={logoUrl} alt="Matchwell" style={{ height:48, width:"auto", objectFit:"contain" }} onError={e=>e.target.style.display="none"}/>
          </div>
          <div className="desk-nav" style={{ display:"flex", gap:6 }}>
            {["Shop","Kurti","Western Wear","Cord Set","3 Piece Set"].map(l=>(
              <button key={l} onClick={()=>l==="Shop"?onNav("shop"):onNav("shop",l)}
                style={{ fontSize:11, letterSpacing:1, textTransform:"uppercase", color:"#1a1a2e", cursor:"pointer", padding:"6px 14px", borderRadius:20, fontWeight:700, border:"1px solid rgba(0,0,0,0.75)", background:"rgba(255,255,255,0.85)", backdropFilter:"blur(8px)", WebkitBackdropFilter:"blur(8px)", fontFamily:"'DM Sans',sans-serif", transition:"all 0.2s" }}
                onMouseEnter={e=>{e.currentTarget.style.color="white"; e.currentTarget.style.background="#EC4899"; e.currentTarget.style.borderColor="#EC4899";}}
                onMouseLeave={e=>{e.currentTarget.style.color="#1a1a2e"; e.currentTarget.style.background="rgba(255,255,255,0.85)"; e.currentTarget.style.borderColor="rgba(0,0,0,0.75)";}}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
          {showSearch && (
            <div style={{ position:"relative", display:"flex", alignItems:"center", gap:6 }}>
              <input autoFocus value={sq} onChange={e=>setSq(e.target.value)}
                onKeyDown={e=>{ if(e.key==="Enter"){onNav("shop",null,sq); setShowSearch(false); setSq(""); } else if(e.key==="Escape"){ setShowSearch(false); setSq(""); } }}
                placeholder="Search..."
                style={{ width:"clamp(130px,18vw,200px)", padding:"8px 14px", ...glassBtn, borderRadius:50, fontSize:12, outline:"none" }}/>
              <button onClick={()=>{ setShowSearch(false); setSq(""); }}
                style={{ ...glassBtn, width:30, height:30, borderRadius:"50%", border:"none", cursor:"pointer", fontSize:14, color:"#1a1a2e", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                ✕
              </button>
              {sq.trim().length>1 && (()=>{
                const matches = products.filter(p=>p.name?.toLowerCase().includes(sq.toLowerCase())).slice(0,6);
                if (!matches.length) return null;
                return (
                  <div style={{ position:"absolute", top:"calc(100% + 8px)", left:0, right:0, ...glass, borderRadius:16, overflow:"hidden", zIndex:1001, minWidth:220 }}>
                    {matches.map(p=>(
                      <div key={p.id} onClick={()=>{ onNav("product", p.id); setShowSearch(false); setSq(""); }}
                        style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", cursor:"pointer", borderBottom:"1px solid rgba(0,0,0,0.05)" }}
                        onMouseEnter={e=>e.currentTarget.style.background="rgba(236,72,153,0.06)"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        {p.img && <img src={p.img} alt="" style={{ width:32, height:38, objectFit:"cover", borderRadius:6 }}/>}
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontSize:12, fontWeight:600, color:"#1a1a2e", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</div>
                          <div style={{ fontSize:11, color:"#EC4899", fontWeight:700 }}>₹{p.price}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}
          <button onClick={()=>setShowSearch(!showSearch)} style={iconBtnStyle}
            onMouseEnter={e=>e.currentTarget.style.transform="scale(1.08)"} onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}>🔍</button>
          <button onClick={startVoice} title="Voice Search" style={iconBtnStyle}
            onMouseEnter={e=>e.currentTarget.style.transform="scale(1.08)"} onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}>🎤</button>
          {/* Account/login — hidden on mobile since the bottom dock already has it */}
          <button onClick={()=>onNav("account")} className="nav-account-icon" style={iconBtnStyle}
            onMouseEnter={e=>e.currentTarget.style.transform="scale(1.08)"} onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}>
            {user ? "👤" : "🔐"}
            {user && <span style={{ position:"absolute", top:1, right:2, width:8, height:8, background:"#10B981", borderRadius:"50%", border:"2px solid rgba(255,255,255,0.9)" }}/>}
          </button>
          <button onClick={()=>onNav("wishlist")} style={iconBtnStyle}
            onMouseEnter={e=>e.currentTarget.style.transform="scale(1.08)"} onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}>
            ❤️{wish.length > 0 && <sup style={{ position:"absolute", top:-2, right:-2, fontSize:9, fontWeight:800, color:"white", background:"#EF4444", borderRadius:"50%", width:15, height:15, display:"flex", alignItems:"center", justifyContent:"center" }}>{wish.length}</sup>}
          </button>
          <button onClick={()=>onNav("cart")} style={{ background:"linear-gradient(135deg,rgba(236,72,153,0.9),rgba(124,58,237,0.9))", backdropFilter:"blur(10px)", color:"white", border:"1px solid rgba(255,255,255,0.4)", padding:"9px 16px", borderRadius:50, fontSize:12, fontWeight:800, cursor:"pointer", display:"flex", alignItems:"center", gap:6, boxShadow:"0 4px 16px rgba(236,72,153,0.35), inset 0 1px 0 rgba(255,255,255,0.4)", transition:"transform 0.15s ease" }}
            onMouseEnter={e=>e.currentTarget.style.transform="scale(1.05)"} onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}>
            🛒{cartQty > 0 && <span style={{ background:"white", color:"#EC4899", borderRadius:"50%", width:18, height:18, fontSize:10, fontWeight:800, display:"inline-flex", alignItems:"center", justifyContent:"center" }}>{cartQty}</span>}
          </button>
        </div>
      </nav>
      )}

      {/* Bottom dock — floating liquid-glass pill, always visible on mobile (not affected by scroll-hide) */}
      <div id="mobNav" style={{ display:"none", position:"fixed", bottom:14, left:14, right:14, ...glass, borderRadius:28, zIndex:999, padding:"8px 4px" }}>
        {[
          { icon:"🏠", l:"Home",    pg:"home" },
          { icon:"🔍", l:"Shop",    pg:"shop" },
          { icon:"🛒", l:"Cart",    pg:"cart",     badge:cartQty },
          { icon:"❤️", l:"Saved",   pg:"wishlist", badge:wish.length },
          { icon:"👤", l:"Account", pg:"account" },
        ].map(item=>(
          <button key={item.l} onClick={()=>onNav(item.pg)}
            style={{ flex:1, display:"inline-flex", flexDirection:"column", alignItems:"center", gap:2, cursor:"pointer", border:"none", background:page===item.pg?"rgba(236,72,153,0.12)":"none", borderRadius:18, padding:"6px 0", fontSize:9, letterSpacing:1, textTransform:"uppercase", color:page===item.pg?"#EC4899":"#666", fontFamily:"'DM Sans',sans-serif", fontWeight:page===item.pg?700:500, position:"relative", transition:"all 0.2s" }}>
            <span style={{ fontSize:19, lineHeight:1 }}>{item.icon}</span>
            {item.badge > 0 && <span style={{ position:"absolute", top:2, right:"calc(50% - 20px)", background:"#EF4444", color:"white", borderRadius:"50%", width:15, height:15, fontSize:8, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", border:"1.5px solid white" }}>{item.badge}</span>}
            {item.l}
          </button>
        ))}
      </div>
    </>
  );
}
